import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  HStack,
  Icon,
  IconButton,
  Image as ChakraImage,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  useClipboard,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { css } from '@emotion/react'
import { Base64 } from 'js-base64'
import _ from 'lodash'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import type { IconType } from 'react-icons'
import { AiOutlineCopy, AiOutlineInfoCircle } from 'react-icons/ai'
import { BsTags, BsThreeDots } from 'react-icons/bs'
import { CgPlayListAdd, CgPlayListRemove, CgRename } from 'react-icons/cg'
import { FaRegBell } from 'react-icons/fa'
import { GrImage } from 'react-icons/gr'
import {
  HiOutlineDocumentDownload,
  HiOutlineFolderAdd,
  HiOutlineFolderRemove,
} from 'react-icons/hi'
import { HiMiniBookmark } from 'react-icons/hi2'
import { ImEmbed } from 'react-icons/im'
import {
  IoCloseOutline,
  IoImages,
  IoImagesOutline,
  IoLogOutOutline,
  IoMoveSharp,
} from 'react-icons/io5'
import {
  MdCenterFocusWeak,
  MdCollections,
  MdContentCopy,
  MdOutlineLink,
  MdOutlineShare,
  MdPublishedWithChanges,
  MdRestore,
} from 'react-icons/md'
import { RiDeleteBin2Line, RiInboxArchiveLine } from 'react-icons/ri'
import { TbLink } from 'react-icons/tb'
import { TiTags } from 'react-icons/ti'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import {
  checkOperationDisabled,
  checkOperationPermission,
  checkPermission,
  PermissionsEnum,
} from '../../../utils'
import { FeaturesEnum } from '../../../utils/features'
import {
  allowExtensionOperations,
  downloadFileFromUrl,
  isEditableImage,
} from '../../../utils/mediaLibrary'
import { isRestrictedUser } from '../../../utils/roles'
import {
  removeLeadingSlash,
  removeTrailingSlash,
} from '../../../utils/urlHelpers'
import { ACLPermissionEnum } from '../../constants'
import useToast from '../../hooks/useToast'
import type {
  CopyMoveOperations,
  TagOperations,
  ViewableParentSuccessResponse,
} from '../../interfaces'
import { MLContext, UserContext } from '../../providers'
import { MediaLibraryActions } from '../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import {
  AddToMediaCollection,
  RemoveAssetFromMultipleMediaCollections,
  RemoveMultipleAssetsFromMediaCollection,
} from '../MediaCollections'
import DeleteMediaCollectionModal from '../MediaCollections/DeleteMediaCollectionModal'
import BookmarksDrawer from '../NavBar/BookmarksDrawer'
import SavedSearchesDrawer from '../NavBar/SavedSearchesDrawer'
import CopyEmbedURLModal from './CopyEmbedURLModal'
import AutoTagsModal from './Details/AutoTagsModal'
import RemoveBgModal from './Details/RemoveBgModal'
import {
  CopyMoveDrawer,
  CustomFoucsAreaModal,
  DeleteMediaModal,
  DownloadModal,
  PublicLinksModal,
  RenameFileModal,
  RenameMediaCollectionModal,
  ShareAssetModal,
  ShareMediaCollectionModal,
  TagsModal,
} from './MediaOperations'
import DeleteFileVersionModal from './MediaOperations/DeleteFileVersionModal'
import FolderDetailsDrawer from './MediaOperations/FolderDetailDrawer'
import MediaCollectionDetailDrawer from './MediaOperations/MediaCollectionDetailDrawer'
import PublishFilesModal from './MediaOperations/PublishFilesModal'
import RestoreFileVersionModal from './MediaOperations/RestoreFileVersionModal'
import NotificationDrawer from './Notifications/NotificationDrawer'
import PhotoEditor from './PhotoEditor'
import Search from './Search'
import SearchMediaCollection from './SearchMediaCollections'

interface HeaderActionbarItem {
  name: string
  viewConditions: boolean
  icon: IconType
  handleClick(): void
  optionDisabledInfo?():
    | {
        isDisabled: boolean
        remainingLength?: Number
        assetName?: String
        tooltipMessage?: React.ReactElement<any, any>
      }
    | undefined
}

interface Props {
  resetSearch(): void
}

const NotificationBell = ({
  count,
  onOpenNotificationDrawer,
}: {
  count: number
  onOpenNotificationDrawer(): void
}): React.ReactElement => {
  const displayCount = count > 99 ? '99+' : `${count}`
  return (
    <IconButton
      onClick={onOpenNotificationDrawer}
      variant="link"
      css={css`
        position: relative !important;
      `}
      aria-label="Notifications"
      colorScheme="appSlate"
      height="35px"
      width="35px"
      py={2}
      icon={
        <>
          <FaRegBell color="gray.750" size="18px" />
          {count > 0 ? (
            <Box
              minW="18px"
              color="white"
              position="absolute"
              top="2px"
              right="2px"
              fontSize="0.6rem"
              bgColor="red"
              borderRadius="lg"
              zIndex={9999}
              p="1px"
            >
              {displayCount}
            </Box>
          ) : null}
        </>
      }
    />
  )
}

const NoitificationBellMemoized = React.memo(NotificationBell)

const HeaderActionbar = ({ resetSearch }: Props): React.ReactElement | null => {
  const [
    {
      selectedMediaCollections,
      selectedFiles,
      selectedFolders,
      fileList,
      folderList,
      mediaCollectionList,
      refetchFiles,
      currentMediaCollection,
      folderPath,
      viewableParent,
      mediaCollectionRoute,
      unseenNotificationsCount,
    },
    dispatch,
  ] = useContext(MLContext)
  const { t } = useTranslation()
  const {
    dashboardServerURL: directDashboardServerURL,
    isPublicLink,
    urlPrefix,
  } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)
  const dashboardServerURL =
    user?.dashboardServerURL ?? directDashboardServerURL
  const toast = useToast()
  const {
    url,
    params: { folderPathBase64 },
  } = useRouteMatch<{ folderPathBase64?: string }>()
  const [tagOperation, setTagOperation] = useState<TagOperations>('addTagsBulk')
  const [copyMoveOperation, setCopyMoveOperation] =
    useState<CopyMoveOperations>('copy')
  const [searchFocused, setSearchFocused] = useState<boolean>(false)
  const ASSET_IDS_COUNT_LIMIT = 100

  const location = useLocation()
  const history = useHistory()
  const {
    isOpen: isOpenRenameFileModal,
    onOpen: onOpenRenameFileModal,
    onClose: onCloseRenameFileModal,
  } = useDisclosure()

  const {
    isOpen: isOpenPublicLinksModal,
    onOpen: onOpenPublicLinksModal,
    onClose: onClosePublicLinkModal,
  } = useDisclosure()

  const {
    isOpen: isOpenRenameMediaCollectionModal,
    onOpen: onOpenRenameMediaCollectionModal,
    onClose: onCloseRenameMediaCollectionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenPublishFilesModal,
    onOpen: onOpenPublishFilesModal,
    onClose: onClosePublishFilesModal,
  } = useDisclosure()

  const {
    isOpen: isOpenDownloadModal,
    onOpen: onOpenDownloadModal,
    onClose: onCloseDownloadModal,
  } = useDisclosure()

  const {
    isOpen: isOpenTagsModal,
    onOpen: onOpenTagsModal,
    onClose: onCloseTagsModal,
  } = useDisclosure()

  const {
    isOpen: isOpenAutoTagsModal,
    onOpen: onOpenAutoTagsModal,
    onClose: onCloseAutoTagsModal,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteMediaModal,
    onOpen: onOpenDeleteMediaModal,
    onClose: onCloseDeleteMediaModal,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteFileVersionModal,
    onOpen: onOpenDeleteFileVersionModal,
    onClose: onCloseDeleteFileVersionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenRestoreFileVersionModal,
    onOpen: onOpenRestoreFileVersionModal,
    onClose: onCloseRestoreFileVersionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenCopyMoveDrawer,
    onOpen: onOpenCopyMoveDrawer,
    onClose: onCloseCopyMoveDrawer,
  } = useDisclosure()

  const {
    isOpen: isOpenPhotoEditor,
    onOpen: onOpenPhotoEditor,
    onClose: onClosePhotoEditor,
  } = useDisclosure()

  const {
    isOpen: isOpenCustomFoucsAreaModal,
    onOpen: onOpenCustomFoucsAreaModal,
    onClose: onCloseCustomFoucsAreaModal,
  } = useDisclosure()

  const {
    isOpen: isOpenRemoveBgModal,
    onOpen: onOpenRemoveBgModal,
    onClose: onCloseRemoveBgModal,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteMediaCollectionModal,
    onOpen: onOpenDeleteMediaCollectionModal,
    onClose: onCloseDeleteMediaCollectionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenAddToMediaCollection,
    onOpen: onOpenAddToMediaCollection,
    onClose: onCloseAddToMediaCollection,
  } = useDisclosure()

  const {
    isOpen: isOpenShareAssetModal,
    onOpen: onOpenShareAssetModal,
    onClose: onCloseShareAssetModal,
  } = useDisclosure()

  const {
    isOpen: isOpenShareMediaCollectionModal,
    onOpen: onOpenShareMediaCollectionModal,
    onClose: onCloseShareMediaCollectionModal,
  } = useDisclosure()

  const {
    isOpen: isOpenRemoveAssetFromMultipleMediaCollections,
    onOpen: onOpenRemoveAssetFromMultipleMediaCollections,
    onClose: onCloseRemoveAssetFromMultipleMediaCollections,
  } = useDisclosure()

  const {
    isOpen: isOpenRemoveMultipleAssetsFromMediaCollection,
    onOpen: onOpenRemoveMultipleAssetsFromMediaCollection,
    onClose: onCloseRemoveMultipleAssetsFromMediaCollection,
  } = useDisclosure()

  const {
    isOpen: isOpenFolderDetailsDrawer,
    onOpen: onOpenFolderDetailsDrawer,
    onClose: onCloseFolderDetailsDrawer,
  } = useDisclosure()

  const {
    isOpen: isOpenCollectionDetailsDrawer,
    onOpen: onOpenCollectionDetailsDrawer,
    onClose: onCloseCollectionDetailsDrawer,
  } = useDisclosure()

  const {
    isOpen: isOpenNotificationDrawer,
    onOpen: onOpenNotificationDrawer,
    onClose: onCloseNotificationDrawer,
  } = useDisclosure()

  const {
    isOpen: isOpenBookmarks,
    onClose: onCloseBookmarks,
    onOpen: onOpenBookmarks,
  } = useDisclosure()

  const {
    isOpen: isOpenSavedSearches,
    onClose: onCloseSavedSearches,
    onOpen: onOpenSavedSearches,
  } = useDisclosure()

  const [openEmbedModal, handleEmbedModal] = useState(false)

  const { onCopy } = useClipboard(selectedFiles[0]?.url)

  const headerActionbarItems = useMemo<HeaderActionbarItem[]>(() => {
    const isFileVersionSelected =
      selectedFiles.filter(item => item.type === 'file-version').length > 0
    const items: HeaderActionbarItem[] = [
      {
        name: 'Copy',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedMediaCollections.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [
                ACLPermissionEnum.MANAGE,
                ACLPermissionEnum.CONTRIBUTE,
              ],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: AiOutlineCopy,
        handleClick: () => {
          dispatch([
            {
              type: MediaLibraryActions.SET_DRAWER_SEARCH_FILTER,
              payload: '',
            },
            {
              type: MediaLibraryActions.SET_DRAWER_SEARCH_QUERY,
              payload: '',
            },
          ])
          setCopyMoveOperation('move')
          setCopyMoveOperation('copy')
          onOpenCopyMoveDrawer()
        },
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
      },
      {
        name: 'Move',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedMediaCollections.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [ACLPermissionEnum.MANAGE],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: IoMoveSharp,
        handleClick: () => {
          dispatch([
            {
              type: MediaLibraryActions.SET_DRAWER_SEARCH_FILTER,
              payload: '',
            },
            {
              type: MediaLibraryActions.SET_DRAWER_SEARCH_QUERY,
              payload: '',
            },
          ])
          setCopyMoveOperation('copy')
          setCopyMoveOperation('move')
          onOpenCopyMoveDrawer()
        },
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
      },
      {
        name: 'Rename',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: CgRename,
        handleClick: () => {
          onOpenRenameFileModal()
        },
      },
      {
        name: 'Rename',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          !isFileVersionSelected &&
          selectedFiles.length === 0 &&
          selectedFolders.length === 0 &&
          selectedMediaCollections.length === 1 &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: CgRename,
        handleClick: () => {
          onOpenRenameMediaCollectionModal()
        },
      },
      {
        name: t('change_publication_status'),
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(FeaturesEnum.DRAFT_ASSETS) ??
            false) &&
          selectedFiles.length > 0 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [ACLPermissionEnum.MANAGE],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: MdPublishedWithChanges,
        handleClick: onOpenPublishFilesModal,
        optionDisabledInfo: () => {
          if (selectedFiles.length > ASSET_IDS_COUNT_LIMIT) {
            return {
              isDisabled: true,
              tooltipMessage: (
                <Trans t={t} i18nKey="publish_selection_limit_exceeded">
                  You cannot publish or unpublish more than{' '}
                  {{ ASSET_IDS_COUNT_LIMIT }} files at once.
                </Trans>
              ),
            }
          }
          return checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          })
        },
      },
      {
        name: 'Details',
        viewConditions:
          selectedFiles.length === 1 && selectedFolders.length === 0,
        icon: AiOutlineInfoCircle,
        handleClick: () => {
          history.push(`/media-library/detail/${selectedFiles[0].fileId}`)
        },
      },
      {
        name: 'Folder details',
        viewConditions:
          !isPublicLink &&
          selectedFiles.length === 0 &&
          selectedFolders.length === 1 &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
              ACLPermissionEnum.READ,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: AiOutlineInfoCircle,
        handleClick: onOpenFolderDetailsDrawer,
      },
      {
        name: 'Collection details',
        viewConditions:
          !isPublicLink &&
          selectedMediaCollections.length === 1 &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
              ACLPermissionEnum.READ,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: AiOutlineInfoCircle,
        handleClick: onOpenCollectionDetailsDrawer,
      },
      {
        name: 'Add to media collection',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ) ??
            false) &&
          !isFileVersionSelected &&
          (selectedFiles.length > 0 || selectedFolders.length > 0) &&
          selectedMediaCollections.length === 0 &&
          (checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [
                ACLPermissionEnum.MANAGE,
                ACLPermissionEnum.CONTRIBUTE,
              ],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: HiOutlineFolderAdd,
        handleClick: onOpenAddToMediaCollection,
        optionDisabledInfo: () => {
          if (
            selectedFiles.length + selectedFolders.length >
            ASSET_IDS_COUNT_LIMIT
          ) {
            return {
              isDisabled: true,
              tooltipMessage: (
                <Trans
                  t={t}
                  i18nKey="add_to_media_collection_asset_limit_exceeded"
                >
                  You cannot add more than {{ ASSET_IDS_COUNT_LIMIT }} assets at
                  once to media collections.
                </Trans>
              ),
            }
          }
          return checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          })
        },
      },
      {
        name: 'Remove from media collections',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ) ??
            false) &&
          !isFileVersionSelected &&
          [...selectedFiles, ...selectedFolders].length === 1 &&
          selectedMediaCollections.length === 0 &&
          currentMediaCollection === undefined,
        icon: HiOutlineFolderRemove,
        handleClick: onOpenRemoveAssetFromMultipleMediaCollections,
      },
      {
        name: 'Remove from media collection',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ) ??
            false) &&
          !isFileVersionSelected &&
          (selectedFiles.length > 0 || selectedFolders.length > 0) &&
          selectedMediaCollections.length === 0 &&
          currentMediaCollection !== undefined &&
          folderPath === '/' &&
          (isRestrictedUser(user?.roles)
            ? (viewableParent as ViewableParentSuccessResponse).mediaCollection
                ?.permission === ACLPermissionEnum.MANAGE
            : true),
        icon: HiOutlineFolderRemove,
        handleClick: onOpenRemoveMultipleAssetsFromMediaCollection,
        optionDisabledInfo: () => {
          if (
            selectedFiles.length + selectedFolders.length >
            ASSET_IDS_COUNT_LIMIT
          ) {
            return {
              isDisabled: true,
              tooltipMessage: (
                <Trans
                  t={t}
                  i18nKey="remove_from_media_collection_asset_limit_exceeded"
                >
                  You cannot remove more than {{ ASSET_IDS_COUNT_LIMIT }} assets
                  at once from media collections.
                </Trans>
              ),
            }
          }
        },
      },
      {
        name: 'Share',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ) ??
            false) &&
          ((selectedFiles.length === 1 && selectedFolders.length === 0) ||
            (selectedFolders.length === 1 && selectedFiles.length === 0)) &&
          selectedMediaCollections.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: MdOutlineShare,
        handleClick: onOpenShareAssetModal,
      },
      {
        name: 'Share',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          (user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ) ??
            false) &&
          selectedMediaCollections.length === 1 &&
          selectedFiles.length === 0 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: MdOutlineShare,
        handleClick: onOpenShareMediaCollectionModal,
      },
      {
        name: 'Public links',
        viewConditions:
          !isPublicLink &&
          (user?.pricingPlan.featuresList.includes(FeaturesEnum.PUBLIC_LINKS) ??
            false) &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          !isFileVersionSelected &&
          (selectedFiles.length + selectedFolders.length === 1 ||
            selectedMediaCollections.length === 1) &&
          checkOperationPermission({
            user,
            aclPermission: [ACLPermissionEnum.MANAGE],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: MdOutlineLink,
        handleClick: onOpenPublicLinksModal,
      },
      {
        name: 'Download',
        viewConditions:
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected,
        icon: HiOutlineDocumentDownload,
        handleClick: () => {
          const downloadURL = `${removeTrailingSlash(
            dashboardServerURL!,
          )}/${removeLeadingSlash(urlPrefix)}api/v2/files/${
            selectedFiles[0].fileId
          }/download?fileName=${selectedFiles[0].name}`
          downloadFileFromUrl(downloadURL)
        },
      },
      {
        name: 'Download (as zip)',
        viewConditions:
          !isFileVersionSelected &&
          (selectedFiles.length > 1 ||
            selectedFolders.length > 0 ||
            selectedMediaCollections.length > 0) &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
              ACLPermissionEnum.READ,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: RiInboxArchiveLine,
        handleClick: () => {
          onOpenDownloadModal()
        },
      },
      {
        name: 'Copy URL',
        viewConditions:
          selectedFiles.length === 1 && selectedFolders.length === 0,
        icon: MdContentCopy,
        handleClick: () => {
          onCopy()
          toast({
            description: t(
              'copied_url_to_dashboard',
              'Copied URL to clipboard',
            ),
            status: 'success',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        },
      },
      {
        name: 'Embed',
        viewConditions:
          !isPublicLink &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          selectedFiles[0]?.mime?.includes('video') &&
          selectedFiles[0]?.isPublished,
        icon: ImEmbed,
        handleClick: () => {
          handleEmbedModal(true)
        },
      },
      {
        name: 'Image editor',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          isEditableImage(selectedFiles[0]) &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: GrImage,
        handleClick: () => {
          onOpenPhotoEditor()
        },
      },
      {
        name: 'Remove background',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          allowExtensionOperations(selectedFiles[0]) &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: IoImagesOutline,
        handleClick: () => {
          onOpenRemoveBgModal()
        },

        // Sending Payload shoudln't be required, as this object can be
        // accessed in the prevState in Provider
        // payload: selectedFiles[0]
      },
      {
        name: 'Custom focus area',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          isEditableImage(selectedFiles[0]) &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: MdCenterFocusWeak,
        handleClick: () => {
          onOpenCustomFoucsAreaModal()
        },
      },
      {
        name: 'Tags',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: BsTags,
        handleClick: () => {
          setTagOperation('updateTags')
          onOpenTagsModal()
        },
      },
      {
        name: 'Add auto tags',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length === 1 &&
          allowExtensionOperations(selectedFiles[0]) &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: TiTags,
        handleClick: () => {
          onOpenAutoTagsModal()
        },
      },
      {
        name: 'Add bulk tags',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length > 1 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [
                ACLPermissionEnum.MANAGE,
                ACLPermissionEnum.CONTRIBUTE,
              ],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: CgPlayListAdd,
        handleClick: () => {
          setTagOperation('addTagsBulk')
          onOpenTagsModal()
        },
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
      },
      {
        name: 'Remove bulk tags',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedFiles.length > 1 &&
          selectedFolders.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [
                ACLPermissionEnum.MANAGE,
                ACLPermissionEnum.CONTRIBUTE,
              ],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: CgPlayListRemove,
        handleClick: () => {
          setTagOperation('removeTagsBulk')
          onOpenTagsModal()
        },
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [ACLPermissionEnum.READ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
      },
      {
        name: 'Restore version',
        viewConditions:
          !isPublicLink &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: MdRestore,
        handleClick: () => {
          onOpenRestoreFileVersionModal()
        },
      },
      {
        name: 'Delete version',
        viewConditions:
          !isPublicLink &&
          selectedFiles.length === 1 &&
          selectedFolders.length === 0 &&
          isFileVersionSelected &&
          checkOperationPermission({
            user,
            aclPermission: [
              ACLPermissionEnum.CONTRIBUTE,
              ACLPermissionEnum.MANAGE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
        icon: RiDeleteBin2Line,
        handleClick: () => {
          onOpenDeleteFileVersionModal()
        },
      },
      {
        name: 'Delete',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          selectedMediaCollections.length === 0 &&
          !isFileVersionSelected &&
          (checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [ACLPermissionEnum.MANAGE],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: RiDeleteBin2Line,
        handleClick: onOpenDeleteMediaModal,
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
      },
      {
        name: 'Delete',
        viewConditions:
          !isPublicLink &&
          checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
          }) &&
          !isFileVersionSelected &&
          selectedFiles.length === 0 &&
          selectedFolders.length === 0 &&
          (checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }).isDisabled ||
            checkOperationPermission({
              user,
              aclPermission: [ACLPermissionEnum.MANAGE],
              selectedFiles,
              selectedFolders,
              selectedMediaCollections,
            })),
        icon: RiDeleteBin2Line,
        handleClick: onOpenDeleteMediaCollectionModal,
        optionDisabledInfo: () =>
          checkOperationDisabled({
            user,
            aclPermission: [
              ACLPermissionEnum.READ,
              ACLPermissionEnum.CONTRIBUTE,
            ],
            selectedFiles,
            selectedFolders,
            selectedMediaCollections,
          }),
      },
    ]

    return items.filter(x => x.viewConditions)
  }, [
    selectedFiles,
    selectedFolders,
    selectedMediaCollections,
    user,
    currentMediaCollection,
    folderPath,
    isPublicLink,
    dashboardServerURL,
    urlPrefix,
  ])

  useEffect(() => {
    const queryString = new URLSearchParams(location.search)
    dispatch([
      {
        type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
      },
      {
        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
      },
      {
        type: MediaLibraryActions.SET_VERSION_INCLUDED_IN_SEARCH,
        payload: queryString.has('type'),
      },
    ])
    setSearchFocused(false)
  }, [
    folderPath,
    currentMediaCollection,
    url,
    folderPathBase64,
    location.search,
  ])

  const getTooltipLabel = optionDisabledInf => {
    const assetName = optionDisabledInf?.assetName
    const remainingLength = optionDisabledInf?.remainingLength
    let tooltipLabel
    if (optionDisabledInf?.tooltipMessage) {
      tooltipLabel = optionDisabledInf.tooltipMessage
    } else if (remainingLength === 0) {
      tooltipLabel = (
        <Trans t={t} i18nKey="context_menu_disable_tooltip_one_asset">
          You do not have sufficient permissions to perform this action on{' '}
          {{ assetName }}.
        </Trans>
      )
    } else {
      tooltipLabel = (
        <Trans t={t} i18nKey="context_menu_disable_tooltip">
          You do not have sufficient permissions to perform this action on{' '}
          {{ assetName }} and {{ remainingLength }} more items.
        </Trans>
      )
    }

    return tooltipLabel
  }

  return (
    <>
      <HStack width="full" spacing="10" justifyContent="space-between">
        {!isPublicLink ? (
          <Box width="50%">
            {folderPath === '/' &&
            !currentMediaCollection &&
            mediaCollectionRoute ? (
              <SearchMediaCollection
                searchFocused={searchFocused}
                setSearchFocused={setSearchFocused}
                resetSearch={resetSearch}
              />
            ) : (
              <Search
                searchFocused={searchFocused}
                setSearchFocused={setSearchFocused}
                resetSearch={resetSearch}
              />
            )}
          </Box>
        ) : (
          <Box width="50%">
            <ChakraImage
              src="https://ik.imagekit.io/ikmedia/logo/light_T4buIzohVH.svg"
              alt="ImageKit logo"
              height="35px"
            />
          </Box>
        )}

        {(viewableParent as ViewableParentSuccessResponse).asset ||
        (viewableParent as ViewableParentSuccessResponse).mediaCollection ? (
          <VStack alignItems="end">
            <HStack spacing="6">
              <HStack spacing="4">
                <Checkbox
                  size="lg"
                  isChecked={
                    folderPath === '/' &&
                    !currentMediaCollection &&
                    mediaCollectionRoute
                      ? selectedMediaCollections.length !== 0 &&
                        selectedMediaCollections.length ===
                          mediaCollectionList.length
                      : [...selectedFiles, ...selectedFolders].length !== 0 &&
                        [...selectedFiles, ...selectedFolders].length ===
                          [...fileList, ...folderList].length
                  }
                  isIndeterminate={
                    folderPath === '/' &&
                    !currentMediaCollection &&
                    mediaCollectionRoute
                      ? selectedMediaCollections.length > 0 &&
                        selectedMediaCollections.length !==
                          mediaCollectionList.length
                      : [...selectedFiles, ...selectedFolders].length > 0 &&
                        [...selectedFiles, ...selectedFolders].length !==
                          [...fileList, ...folderList].length
                  }
                  onChange={e => {
                    if (!e.target.checked) {
                      dispatch([
                        {
                          type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                        },
                        {
                          type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                        },
                      ])
                    } else {
                      folderPath === '/' &&
                      !currentMediaCollection &&
                      mediaCollectionRoute
                        ? dispatch([
                            {
                              type: MediaLibraryActions.SET_SELECTED_MEDIA_COLLECTIONS,
                              payload: mediaCollectionList,
                            },
                            {
                              type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                            },
                          ])
                        : dispatch([
                            {
                              type: MediaLibraryActions.SET_SELECTED_FILES,
                              payload: fileList,
                            },
                            {
                              type: MediaLibraryActions.SET_SELECTED_FOLDERS,
                              payload: folderList,
                            },
                            {
                              type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                            },
                          ])
                    }
                  }}
                >
                  Select All
                </Checkbox>

                {[...selectedFiles, ...selectedFolders].length > 0 ||
                selectedMediaCollections.length > 0 ? (
                  <>
                    <Divider orientation="vertical" height="4" />
                    <Text color="appGreenishTeal" fontWeight="medium">
                      {folderPath === '/' &&
                      (!currentMediaCollection ||
                        selectedMediaCollections.length > 0) &&
                      mediaCollectionRoute
                        ? selectedMediaCollections.length
                        : [...selectedFiles, ...selectedFolders].length}{' '}
                      items selected
                    </Text>
                  </>
                ) : null}
              </HStack>
              {!isPublicLink && (
                <NoitificationBellMemoized
                  count={unseenNotificationsCount}
                  onOpenNotificationDrawer={onOpenNotificationDrawer}
                />
              )}
              {[
                ...selectedFiles,
                ...selectedFolders,
                ...selectedMediaCollections,
              ].length !== 0 ? (
                <ButtonGroup variant="link" spacing="4">
                  {headerActionbarItems
                    .slice(0, 2)
                    .map(({ name, icon, handleClick, optionDisabledInfo }) => {
                      const optionDisabledInf = optionDisabledInfo
                        ? optionDisabledInfo()
                        : undefined

                      const tooltipLabel = getTooltipLabel(optionDisabledInf)
                      return (
                        <Tooltip
                          isDisabled={!optionDisabledInf?.isDisabled}
                          label={tooltipLabel}
                          shouldWrapChildren
                          placement="bottom"
                          hasArrow
                          key={name}
                        >
                          <Button
                            key={`header-action-bar-item-${name}`}
                            colorScheme="appSlate"
                            leftIcon={<Icon as={icon} boxSize="1rem" />}
                            fontWeight="normal"
                            onClick={handleClick}
                            isDisabled={optionDisabledInf?.isDisabled}
                          >
                            {name}
                          </Button>
                        </Tooltip>
                      )
                    })}
                  {headerActionbarItems.length > 3 ? (
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        rightIcon={<BsThreeDots />}
                        minWidth="initial"
                      />
                      <Portal>
                        <MenuList zIndex="popover">
                          {headerActionbarItems
                            .slice(2)
                            .map(
                              ({
                                name,
                                icon,
                                handleClick,
                                optionDisabledInfo,
                              }) => {
                                const optionDisabledInf = optionDisabledInfo
                                  ? optionDisabledInfo()
                                  : undefined
                                const tooltipLabel =
                                  getTooltipLabel(optionDisabledInf)
                                return (
                                  <Tooltip
                                    isDisabled={!optionDisabledInf?.isDisabled}
                                    label={tooltipLabel}
                                    shouldWrapChildren
                                    placement="left"
                                    hasArrow
                                    key={`header-action-bar-item-${name}`}
                                    openDelay={150}
                                  >
                                    <MenuItem
                                      color={
                                        name === 'Delete' ||
                                        name === 'Delete version'
                                          ? 'appBrick'
                                          : 'auto'
                                      }
                                      key={`header-action-bar-item-${name}`}
                                      icon={<Icon as={icon} boxSize="1rem" />}
                                      onClick={handleClick}
                                      isDisabled={optionDisabledInf?.isDisabled}
                                      isFocusable={true}
                                    >
                                      {name}
                                    </MenuItem>
                                  </Tooltip>
                                )
                              },
                            )}
                        </MenuList>
                      </Portal>
                    </Menu>
                  ) : (
                    headerActionbarItems
                      .slice(2, 3)
                      .map(
                        ({ name, icon, handleClick, optionDisabledInfo }) => {
                          const optionDisabledInf = optionDisabledInfo
                            ? optionDisabledInfo()
                            : undefined
                          const tooltipLabel =
                            getTooltipLabel(optionDisabledInf)

                          return (
                            <Tooltip
                              isDisabled={!optionDisabledInf?.isDisabled}
                              label={tooltipLabel}
                              shouldWrapChildren
                              placement="bottom"
                              hasArrow
                              key={name}
                            >
                              <Button
                                key={`header-action-bar-item-${name}`}
                                colorScheme="appSlate"
                                leftIcon={<Icon as={icon} boxSize="1rem" />}
                                fontWeight="normal"
                                onClick={handleClick}
                                isDisabled={optionDisabledInf?.isDisabled}
                              >
                                {name}
                              </Button>
                            </Tooltip>
                          )
                        },
                      )
                  )}
                </ButtonGroup>
              ) : null}
              {window.isMediaLibraryWidget ? (
                <HStack spacing="2">
                  {mediaCollectionRoute ? (
                    <Tooltip
                      label={t('go_to_media_library', 'Go to media library')}
                    >
                      <Button
                        size="sm"
                        leftIcon={<IoImages />}
                        onClick={() => {
                          history.push('/media-library')
                        }}
                      >
                        {t('media_library', 'Media Library')}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      label={t(
                        'go_to_media_collection',
                        'Go to media collection',
                      )}
                    >
                      <Button
                        size="sm"
                        leftIcon={<MdCollections />}
                        onClick={() => {
                          history.push('/media-collections')
                        }}
                      >
                        {t('media_collection', 'Media collection')}
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip label={t('saved_searches', 'Saved searches')}>
                    <IconButton
                      size="sm"
                      aria-label={t('saved_searches', 'Saved searches')}
                      icon={<HiMiniBookmark />}
                      onClick={() => {
                        onCloseBookmarks()

                        if (isOpenSavedSearches) {
                          onCloseSavedSearches()
                        } else {
                          onOpenSavedSearches()
                        }
                      }}
                    />
                  </Tooltip>
                  <Tooltip label={t('bookmarks', 'Bookmarks')}>
                    <IconButton
                      size="sm"
                      aria-label={t('bookmarks', 'Bookmarks')}
                      icon={<TbLink />}
                      onClick={() => {
                        onCloseSavedSearches()

                        if (isOpenBookmarks) {
                          onCloseBookmarks()
                        } else {
                          onOpenBookmarks()
                        }
                      }}
                    />
                  </Tooltip>
                  <ButtonGroup size="sm" isAttached>
                    <Button
                      colorScheme="appSlate"
                      leftIcon={<Icon as={IoLogOutOutline} />}
                      fontWeight="normal"
                      borderRightRadius="0"
                      as={Link}
                      href={`/logout?isMediaLibraryWidget=${
                        window.isMediaLibraryWidget
                      }${
                        window.widgetHost
                          ? `&widgetHost=${window.widgetHost}`
                          : ''
                      }${
                        window.isMediaLibraryWidget
                          ? '&redirectTo=media-library-widget'
                          : ''
                      }${
                        !_.isEmpty(window.mlWidgetSettings?.initialView)
                          ? `&mlWidgetInitialView=${Base64.encode(
                              JSON.stringify(
                                window.mlWidgetSettings?.initialView,
                              ),
                            )}`
                          : ''
                      }`}
                    >
                      {t('logout', 'Logout')}
                    </Button>
                    <Button
                      colorScheme="appSlate"
                      leftIcon={<Icon as={IoCloseOutline} />}
                      fontWeight="normal"
                      variant="outline"
                      borderLeftRadius="0"
                      onClick={() => {
                        if (window.self !== window.top) {
                          window.parent.postMessage(
                            { eventType: 'CLOSE_MEDIA_LIBRARY_WIDGET' },
                            window.widgetHost ?? document.referrer,
                          )
                        } else {
                          console.log(
                            '[DEBUG]',
                            'Simulating response to parent',
                            {
                              eventType: 'CLOSE_MEDIA_LIBRARY_WIDGET',
                              widgetHost:
                                window.widgetHost ?? document.referrer,
                            },
                          )
                        }
                      }}
                    >
                      {t('close', 'Close')}
                    </Button>
                  </ButtonGroup>
                </HStack>
              ) : null}
            </HStack>
          </VStack>
        ) : null}
      </HStack>

      {!isPublicLink ? (
        <FolderDetailsDrawer
          clientNumber={user?.clientNumber}
          folder={selectedFolders[0]}
          isOpen={isOpenFolderDetailsDrawer}
          onClose={onCloseFolderDetailsDrawer}
        />
      ) : null}
      {isOpenCollectionDetailsDrawer && (
        <MediaCollectionDetailDrawer
          clientNumber={user?.clientNumber}
          isOpen={isOpenCollectionDetailsDrawer}
          mediaCollection={selectedMediaCollections[0]}
          onClose={onCloseCollectionDetailsDrawer}
        />
      )}
      <AddToMediaCollection
        isOpen={isOpenAddToMediaCollection}
        onClose={onCloseAddToMediaCollection}
        onOpen={onOpenAddToMediaCollection}
      />
      {isOpenRenameFileModal && (
        <RenameFileModal
          isOpen={isOpenRenameFileModal}
          onClose={onCloseRenameFileModal}
        />
      )}
      {isOpenDownloadModal && (
        <DownloadModal
          isOpen={isOpenDownloadModal}
          onClose={onCloseDownloadModal}
        />
      )}
      {isOpenDeleteMediaModal && (
        <DeleteMediaModal
          isOpen={isOpenDeleteMediaModal}
          onClose={onCloseDeleteMediaModal}
        />
      )}
      {isOpenTagsModal && (
        <TagsModal
          operation={tagOperation}
          isOpen={isOpenTagsModal}
          onClose={onCloseTagsModal}
        />
      )}
      {isOpenAutoTagsModal && (
        <AutoTagsModal
          fileId={selectedFiles[0]?.fileId}
          isOpen={isOpenAutoTagsModal}
          onClose={onCloseAutoTagsModal}
          getFileDetails={refetchFiles}
        />
      )}
      {isOpenCopyMoveDrawer && (
        <CopyMoveDrawer
          operation={copyMoveOperation}
          isOpen={isOpenCopyMoveDrawer}
          onClose={onCloseCopyMoveDrawer}
        />
      )}

      <CustomFoucsAreaModal
        isOpen={isOpenCustomFoucsAreaModal}
        onClose={onCloseCustomFoucsAreaModal}
        fileDetails={selectedFiles[0]}
        getFileDetails={refetchFiles}
      />

      {isOpenRemoveBgModal && (
        <RemoveBgModal
          isOpen={isOpenRemoveBgModal}
          onClose={onCloseRemoveBgModal}
          fileId={selectedFiles[0]?.fileId}
        />
      )}
      {isOpenDeleteFileVersionModal && (
        <DeleteFileVersionModal
          isOpen={isOpenDeleteFileVersionModal}
          onClose={onCloseDeleteFileVersionModal}
        />
      )}
      {isOpenRestoreFileVersionModal && (
        <RestoreFileVersionModal
          isOpen={isOpenRestoreFileVersionModal}
          onClose={onCloseRestoreFileVersionModal}
        />
      )}
      {isOpenDeleteMediaCollectionModal && (
        <DeleteMediaCollectionModal
          isOpen={isOpenDeleteMediaCollectionModal}
          onClose={onCloseDeleteMediaCollectionModal}
        />
      )}
      {isOpenRemoveAssetFromMultipleMediaCollections && (
        <RemoveAssetFromMultipleMediaCollections
          isOpen={isOpenRemoveAssetFromMultipleMediaCollections}
          onClose={onCloseRemoveAssetFromMultipleMediaCollections}
        />
      )}
      {isOpenRemoveMultipleAssetsFromMediaCollection && (
        <RemoveMultipleAssetsFromMediaCollection
          isOpen={isOpenRemoveMultipleAssetsFromMediaCollection}
          onClose={onCloseRemoveMultipleAssetsFromMediaCollection}
        />
      )}
      {isOpenRenameMediaCollectionModal && (
        <RenameMediaCollectionModal
          isOpen={isOpenRenameMediaCollectionModal}
          onClose={onCloseRenameMediaCollectionModal}
        />
      )}
      {isOpenShareAssetModal && (
        <ShareAssetModal
          isOpen={isOpenShareAssetModal}
          onClose={onCloseShareAssetModal}
          assetId={selectedFiles[0]?.fileId ?? selectedFolders[0]?.folderId}
          assetName={selectedFiles[0]?.name ?? selectedFolders[0]?.name}
          assetType={selectedFiles[0]?.type ?? selectedFolders[0]?.type}
        />
      )}
      {isOpenShareMediaCollectionModal && (
        <ShareMediaCollectionModal
          isOpen={isOpenShareMediaCollectionModal}
          onClose={onCloseShareMediaCollectionModal}
          assetId={selectedMediaCollections[0]?.mediaCollectionId}
          assetName={selectedMediaCollections[0]?.name}
        />
      )}
      {isOpenPublicLinksModal && (
        <PublicLinksModal
          assetId={selectedFiles[0]?.fileId ?? selectedFolders[0]?.folderId}
          assetType={selectedFiles[0]?.type ?? selectedFolders[0]?.type}
          assetName={selectedFiles[0]?.name ?? selectedFolders[0]?.name}
          isOpen={isOpenPublicLinksModal}
          onClose={onClosePublicLinkModal}
        />
      )}
      {isOpenPublishFilesModal && (
        <PublishFilesModal
          isOpen={isOpenPublishFilesModal}
          onClose={onClosePublishFilesModal}
        />
      )}
      {!isPublicLink && isOpenNotificationDrawer && (
        <NotificationDrawer
          isOpen={isOpenNotificationDrawer}
          onClose={onCloseNotificationDrawer}
        />
      )}
      {isOpenPhotoEditor && (
        <Portal>
          <PhotoEditor onClose={onClosePhotoEditor} />
        </Portal>
      )}
      {openEmbedModal && selectedFiles && selectedFiles.length > 0 && (
        <CopyEmbedURLModal
          file={selectedFiles[0]}
          openEmbedModal={openEmbedModal}
          handleEmbedModal={handleEmbedModal}
        />
      )}
      {isOpenSavedSearches && (
        <SavedSearchesDrawer
          isOpenSavedSearches={isOpenSavedSearches}
          onCloseSavedSearches={onCloseSavedSearches}
        />
      )}
      {isOpenBookmarks && (
        <BookmarksDrawer
          isOpenBookmarks={isOpenBookmarks}
          onCloseBookmarks={onCloseBookmarks}
        />
      )}
    </>
  )
}

export default HeaderActionbar
